import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { createTool, updateTool } from "../services/ToolService";
import { Tool } from "../types/tool.types";
import schema from "./schema";

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** When null, create. When set, edit that tool. */
  editingTool: Tool | null;
};

export function ToolCreateModal({
  show,
  onClose,
  onSuccess,
  editingTool,
}: Props) {
  const isEdit = editingTool != null;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editingTool?.name ?? "",
      isActive: editingTool?.isActive ?? true,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        if (isEdit && editingTool) {
          const res = await updateTool(editingTool.id, {
            name: values.name.trim(),
            isActive: values.isActive,
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          }
        } else {
          const res = await createTool({
            name: values.name.trim(),
            isActive: values.isActive,
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit ? "Failed to update tool" : "Failed to create tool"),
        );
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit tool" : "Add tool"}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Name must be unique. Tools referenced by a checkout record cannot be
          deleted until unused.
        </p>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label className="form-label" htmlFor="tool-modal-name">
              Name
            </Label>
            <Input
              id="tool-modal-name"
              name="name"
              type="text"
              className="form-control"
              placeholder="e.g. Drill, Wrench Set"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>
          <div className="form-check form-switch form-switch-md mb-4">
            <Input
              className="form-check-input"
              type="checkbox"
              id="tool-modal-active"
              name="isActive"
              checked={formik.values.isActive}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <Label className="form-check-label" htmlFor="tool-modal-active">
              Active (shown in selectors)
            </Label>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting
                ? isEdit
                  ? "Saving…"
                  : "Creating…"
                : isEdit
                  ? "Update"
                  : "Create"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
